import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export interface Tag {
  id: number;
  name: string;
}

export interface Article {
  id?: number;
  title: string;
  author: string;
  author_avatar?: string;
  on_behalf_of?: string;
  content: string;
  status?: 'draft' | 'published' | 'planned' | 'archived';
  created_at?: string;
  updated_at?: string | null;
  locked_by?: string;
  locked_at?: string;
  tags?: string;
  release_at?: string | null;
}

export interface NewsConfig {
  rocketChatUrl: string;
  imagesizeInMb: number;
}

export interface TagManagementConfig {
  allowedGroups: string[];
  allowedUsers: string[];
}

export interface SuperEditorConfig {
  allowedGroups: string[];
  allowedUsers: string[];
}

export interface Member {
  id?: number;
  name: string;
  image_url: string;
  article_id: number;
  created_at?: string | null;
}

export interface PaginatedArticles {
  articles: Article[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface NewsletterStatus {
  subscribed: boolean;
}

export interface NewsApi {
  getPublishedArticles(): Promise<Article[]>;
  getPublishedArticlesPaginated(page: number, pageSize: number, tag?: string | null): Promise<PaginatedArticles>;
  getAllArticles(): Promise<Article[]>;
  createArticle(article: Article): Promise<Article>;
  updateArticle(id: number, article: Partial<Article>): Promise<void>;
  deleteArticle(id: number): Promise<void>;
  lockArticle(id: number, user: string): Promise<void>;
  unlockArticle(id: number, user: string): Promise<void>;
  forceUnlockArticle(id: number): Promise<void>;
  uploadImage(file: File): Promise<string>;
  getImageUrl(url: string): Promise<string>;
  getArticleById(id: number): Promise<Article>;
  getTags(): Promise<Tag[]>;
  getUsedTags(): Promise<string[]>;
  createTag(name: string): Promise<void>;
  deleteTag(id: number): Promise<void>;
  getConfig(): Promise<NewsConfig>;
  getTagManagementConfig(): Promise<TagManagementConfig>;
  getSuperEditorConfig(): Promise<SuperEditorConfig>;
  getLatestMembers(limit?: number): Promise<Member[]>;
  getAllMembers(): Promise<Member[]>;
  createMember(member: Omit<Member, 'id'>): Promise<Member>;
  deleteMember(id: number): Promise<void>;
  getNewsletterStatus(): Promise<NewsletterStatus>;
  subscribeToNewsletter(): Promise<void>;
  unsubscribeFromNewsletter(): Promise<void>;
}

export const newsApiRef = createApiRef<NewsApi>({
  id: 'plugin.news.service',
});

export class NewsApiClient implements NewsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl() {
    return await this.discoveryApi.getBaseUrl('news');
  }

  /** central function for all JSON-based API calls Handles error handling and response parsing **/
  private async fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await this.fetchApi.fetch(url, options);

    if (!response.ok) {
      let errorMessage = await response.text();
      // Try to extract a clean error message from the backend JSON (e.g. 409 Conflict)
      try {
        const errorJson = JSON.parse(errorMessage);
        if (errorJson?.message) {
          errorMessage = errorJson.message;
        }
      } catch (e) {
        // Fallback: It was not JSON, we use the plain text
      }
      throw new Error(`API error (${response.status}): ${errorMessage}`);
    }

    // For DELETE requests, the backend often sends 204 No Content (without body).
    // A response.json() would crash here.
    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return await response.json();
  }

  async getPublishedArticles(): Promise<Article[]> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<Article[]>(`${baseUrl}/news`);
  }

  async getPublishedArticlesPaginated(page: number, pageSize: number, tag?: string | null): Promise<PaginatedArticles> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });
    if (tag) params.append('tag', tag);

    return this.fetchJson<PaginatedArticles>(`${baseUrl}/news/paginated?${params.toString()}`);
  }

  async getAllArticles(): Promise<Article[]> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<Article[]>(`${baseUrl}/admin/all`);
  }

  async createArticle(article: Article): Promise<Article> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<Article>(`${baseUrl}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(article),
    });
  }

  async updateArticle(id: number, article: Partial<Article>): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(article),
    });
  }

  async deleteArticle(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/${id}`, { method: 'DELETE' });
  }

  async lockArticle(id: number, user: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/${id}/lock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user }),
    });
  }

  async unlockArticle(id: number, user: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/${id}/unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user }),
    });
  }

  async forceUnlockArticle(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/${id}/force-unlock`, {
      method: 'POST',
    });
  }

  async uploadImage(file: File): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    const formData = new FormData();
    formData.append('image', file);

    const data = await this.fetchJson<{ url: string }>(`${baseUrl}/upload`, {
      method: 'POST',
      body: formData, // Content-Type is automatically set by the browser for FormData
    });

    return `${baseUrl}/${data.url}`;
  }

  async getImageUrl(relativeUrl: string): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    const fetchUrl = relativeUrl.startsWith('http') ? relativeUrl : `${baseUrl}/${relativeUrl}`;

    // We deliberately do NOT use fetchJson here, as we expect a Blob (image) and not JSON.
    const response = await this.fetchApi.fetch(fetchUrl);

    if (!response.ok) {
      throw new Error(`Failed to load image (${response.status})`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async getArticleById(id: number): Promise<Article> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<Article>(`${baseUrl}/${id}`);
  }

  async getTags(): Promise<Tag[]> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<Tag[]>(`${baseUrl}/tags`);
  }

  async getUsedTags(): Promise<string[]> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<string[]>(`${baseUrl}/tags/used`);
  }

  async createTag(name: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
  }

  async deleteTag(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/tags/${id}`, { method: 'DELETE' });
  }

  async getConfig(): Promise<NewsConfig> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<NewsConfig>(`${baseUrl}/config`);
  }

  async getTagManagementConfig(): Promise<TagManagementConfig> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<TagManagementConfig>(`${baseUrl}/tag-management-config`);
  }

  async getSuperEditorConfig(): Promise<SuperEditorConfig> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<SuperEditorConfig>(`${baseUrl}/super-editor-config`);
  }

  async getLatestMembers(limit: number = 5): Promise<Member[]> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<Member[]>(`${baseUrl}/members/latest?limit=${limit}`);
  }

  async getAllMembers(): Promise<Member[]> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<Member[]>(`${baseUrl}/members`);
  }

  async createMember(member: Omit<Member, 'id'>): Promise<Member> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<Member>(`${baseUrl}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(member),
    });
  }

  async deleteMember(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/members/${id}`, { method: 'DELETE' });
  }

  async getNewsletterStatus(): Promise<NewsletterStatus> {
    const baseUrl = await this.getBaseUrl();
    return this.fetchJson<NewsletterStatus>(`${baseUrl}/newsletter/status`);
  }

  async subscribeToNewsletter(): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/newsletter/subscribe`, { method: 'POST' });
  }

  async unsubscribeFromNewsletter(): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    await this.fetchJson<void>(`${baseUrl}/newsletter/unsubscribe`, { method: 'POST' });
  }
}
